import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { io } from 'socket.io-client';
import PbxContext from '../../context/PbxContext';
import Loading from '../../components/Loading/LoadingModule';
import EndpointsList from '../../components/Endpoints/EndpointsList';
import WebRTC from './components/WebRTC';
import Callcenter from '../Callcenter'
import { fetchEndpoints, accessLocalStorage, exportSelectAllEndpoints, getMusicOnHoldData } from '../../services';
import './styles.css';


function Home() {
  const [loading, setLoading] = useState(true);
  const getItensStateGlobal = useContext(PbxContext);
  const { endpoints, setEndpoints, ipEndpoints, setIpEndpoints, setActiveCallsByEndpoint } = getItensStateGlobal;

  const history = useHistory();

  const getAllEndpointsConfig = async () => {
    const endpointsList = await exportSelectAllEndpoints();

    if(endpointsList === undefined) return setLoading(true);
    const elements = endpointsList.reduce((acc, cur) => {
      const getNameEndpoint = cur.callerid.split(' ');
      acc[`${cur.endpoint}`] = { name: getNameEndpoint.length > 2 ? `${cur.callerid.split(' ')[0]} ${cur.callerid.split(' ')[1]}` : cur.callerid.split(' ')[0], accountcode: cur.tenant };
      return acc;
    },{})
    return elements;
  };

  const getApiEndpoints = useCallback(async () => {
    let endpointsFetcheds = await fetchEndpoints();

    // Agrega par hibrido (NNNN e NNNN-web) em um unico card por ramal.
    // Estado: online se qualquer peer for online. channel_ids: uniao dos dois.
    const byBase = new Map();
    for (const ep of endpointsFetcheds) {
      const base = ep.resource.replace(/-web$/, '');
      if (!byBase.has(base)) {
        byBase.set(base, { ...ep, resource: base, channel_ids: [...(ep.channel_ids || [])] });
        continue;
      }
      const agg = byBase.get(base);
      if (ep.state === 'online') agg.state = 'online';
      agg.channel_ids = [...agg.channel_ids, ...(ep.channel_ids || [])];
    }
    endpointsFetcheds = Array.from(byBase.values()).filter((ep) => !isNaN(Number(ep.resource)));

    const endpointsConfig = await getAllEndpointsConfig();

    let endpointsReceived = endpointsFetcheds.map((element) => {
      endpointsConfig.hasOwnProperty(`${element.resource}`) && !isNaN(element.resource) ? element['name'] = endpointsConfig[`${element.resource}`].name : element.resource;
      if (!element.hasOwnProperty('name')) element.name = element.resource;
      if (endpointsConfig.hasOwnProperty(`${element.resource}`)) element['accountcode'] = endpointsConfig[`${element.resource}`].accountcode;
      delete element.technology
      return element;
    });

    if (endpointsReceived) {
      setLoading(false);

      const dataUser = await accessLocalStorage.getUserLocalStorage();
      let { user: { endpointsviewer, idtenant, role }} = dataUser;

      endpointsReceived = idtenant.includes('vgs') ? endpointsReceived : endpointsReceived.filter((endpoint) => idtenant.includes(endpoint.accountcode));

      const visibility = endpointsviewer !== 'all' ? endpointsviewer.split(',') : [];

      const endpointsForViewer = visibility.length > 0 ? endpointsReceived.filter((endpoint) => {
        if(visibility.includes(endpoint.resource)) return endpoint;
      }) : endpointsReceived;
      setEndpoints(endpointsForViewer);
      
      return ipEndpoints;
    }
  }, [ipEndpoints]);

  const validateUserLogged = async () => {
    const dataUser = await accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    await getApiEndpoints();
  };

  const socketRef = React.useRef(null);
  const endpointsRef = React.useRef(endpoints);
  endpointsRef.current = endpoints;

  useEffect(() => {
    let cancel = false;
    validateUserLogged();
    return () => { cancel = true; };
  }, [ipEndpoints]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
      if (!mounted) return;
      const baseHost = ipRequest.replace(/^https?:\/\//, '').split('/')[0];
      const dbLocal = localStorage;
      const socket = io(`//${baseHost}`, { path: '/io/' });
      socketRef.current = socket;

      socket.on('endpoint-status', (obj) => {
        if (obj.type !== 'ContactStatusChange') return;
        const rawResource = obj.endpoint?.resource;
        if (!rawResource) return;
        // Normaliza peer -web para a base (hybrid endpoints).
        const baseResource = rawResource.replace(/-web$/, '');
        const stateEndpoint = obj.endpoint?.state;

        let ipEndpoint = '';
        if (obj.contact_info?.uri && obj.contact_info.uri.includes('@')) {
          const part = obj.contact_info.uri.split('@')[1] || '';
          ipEndpoint = part.split(':')[0] || part.split(';')[0] || part;
        }
        const sendDbEndpoint = {
          endpoint: baseResource,
          name: endpointsRef.current[`${baseResource}`],
          state: stateEndpoint,
          ip: ipEndpoint,
        };
        if (stateEndpoint !== 'offline') dbLocal.setItem(baseResource, JSON.stringify(sendDbEndpoint));
        if (stateEndpoint === 'offline') dbLocal.removeItem(baseResource);
        setIpEndpoints(sendDbEndpoint);

        // Atualiza array endpoints para que o card re-renderize com novo estado.
        // Offline so prevalece se nao houver channels ativos (evita piscar quando um peer desconecta).
        setEndpoints((prev) => prev.map((ep) => {
          if (ep.resource !== baseResource) return ep;
          const hasChannels = (ep.channel_ids?.length || 0) > 0;
          const newState = stateEndpoint === 'offline' && !hasChannels
            ? 'offline'
            : (stateEndpoint === 'offline' ? ep.state : 'online');
          return { ...ep, state: newState };
        }));
      });
    })();
    return () => {
      mounted = false;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [setIpEndpoints]);

  return (
    <div className="home-container">
      {loading ? (
        <Loading />
      ) : (
        <div className="home-content">
          <div className="home-section endpoints-section">
            <div className="section-header">
              <div className="section-title-wrapper">
                <svg className="section-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {/* <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /> */}
                </svg>
                <div>
                  <h2 className="section-title">Ramais</h2>
                  <p className="section-subtitle">Gerencie, monitore e controle seus ramais</p>
                </div>
              </div>
            </div>
            <div className="section-content">
              <EndpointsList />
            </div>
          </div>

          <div className="home-section callcenter-section">
            <Callcenter />
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
